import { Select } from "@ui/select/Select";
import { CSSProperties, forwardRef } from "react";
import { IStatChannelOption } from "./useStatChannels";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DateRangePicker } from "../conponents/DateRangePicker";
import { CreditCard, Users } from "lucide-react";
import { FilterMultiSelect, type FilterMultiSelectOption } from "./FilterMultiSelect";

const DateControls = styled.div`
	display: flex;
	align-items: flex-end;
	flex: 1 1 360px;
	min-width: 0;
`;

const DateRangeControl = styled.div`
	flex: 1 1 300px;
	min-width: min(100%, 240px);
	max-width: 380px;
	@media (max-width: 560px) { flex-basis: 100%; max-width: none; }
`;

const ScopeControl = styled.div`
	flex: 1 1 220px;
	min-width: min(100%, 200px);
	max-width: 320px;
	@media (max-width: 560px) { flex-basis: 100%; max-width: none; }
`;

const FilterControl = styled.div`
	display: flex;
	&[hidden] { display: none; }
	align-items: flex-end;
	padding-left: 16px;
	border-left: 1px solid #f3f4f6;
	@media (max-width: 560px) { padding-left: 0; border-left: 0; }
`;

interface PeriodSelectorProps {
	dates: string[];
	selectedDate: number;
	onDateChange: (index: number) => void;
	from: string;
	to: string;
	onRangeChange: (from: string, to: string) => void;
	selectedCurrency: string;
	onCurrencyChange: (currency: string) => void;
	channels: IStatChannelOption[];
	channelsBusy: boolean;
	selectedChannel: string;
	onChannelChange: (channel: string) => void;
	showCurrency?: boolean;
	hideFilters?: boolean;
	/** Payment instrument filter; shown when onInstrumentsChange is set. */
	selectedInstruments?: string[];
	onInstrumentsChange?: (instruments: string[]) => void;
	/** Client filter for project dashboards; shown when onClientsChange is set. */
	clients?: FilterMultiSelectOption[];
	clientsBusy?: boolean;
	clientsFailed?: boolean;
	selectedClients?: string[];
	onClientsChange?: (clients: string[]) => void;
	size?: number;
	style?: CSSProperties;
}

const INSTRUMENTS = [
	{ value: "card", key: "stats.filters.instruments.card" },
	{ value: "ap", key: "stats.filters.instruments.ap" },
	{ value: "gp", key: "stats.filters.instruments.gp" },
	{ value: "ob", key: "stats.filters.instruments.ob" },
];

export const PeriodSelector = forwardRef<HTMLDivElement, PeriodSelectorProps>(
	({
		dates, selectedDate, onDateChange, from, to, onRangeChange, selectedCurrency, onCurrencyChange, channels, channelsBusy, selectedChannel, onChannelChange,
		showCurrency = true, hideFilters = false, selectedInstruments = [], onInstrumentsChange, clients = [], clientsBusy = false, clientsFailed = false,
		selectedClients = [], onClientsChange, style,
	}, ref) => {
		const { t } = useTranslation();
		// The parent owns the periods by index, so presets report the index back instead of a computed range.
		const presets = dates.map((label, index) => ({ label, active: index === selectedDate, onSelect: () => onDateChange(index) }));
		return (
			<div
				ref={ref}
				style={{
					flexShrink: 0,
					background: "#fff",
					borderRadius: 12,
					boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
					display: "flex",
					flexWrap: "wrap",
					gap: "12px 16px",
					padding: "12px 16px",
					overflow: "visible",
					transition: "box-shadow 0.2s, border-radius 0.2s, border-bottom-color 0.2s",
					...style,
				}}>
				<DateControls>
					<DateRangeControl>
						<DateRangePicker label={t("dashboard.periods.dateRange")} from={from} to={to} onChange={onRangeChange} presets={presets} />
					</DateRangeControl>
				</DateControls>
				{onInstrumentsChange && <ScopeControl>
					<FilterMultiSelect
						label={t("stats.filters.terminals")}
						icon={<CreditCard size={16} aria-hidden="true" />}
						allLabel={t("stats.filters.allTerminals")}
						options={INSTRUMENTS.map(item => ({ value: item.value, name: t(item.key), detail: item.value.toUpperCase() }))}
						selected={selectedInstruments}
						onChange={onInstrumentsChange}
					/>
				</ScopeControl>}
				{onClientsChange && <ScopeControl>
					<FilterMultiSelect
						label={t("stats.filters.clients")}
						icon={<Users size={16} aria-hidden="true" />}
						allLabel={t("stats.filters.allClients")}
						options={clients}
						busy={clientsBusy}
						error={clientsFailed}
						searchable
						selected={selectedClients}
						onChange={onClientsChange}
					/>
				</ScopeControl>}
				{channels.length > 1 && (
					<FilterControl hidden={hideFilters}>
						<Select<IStatChannelOption>
							sx={{ minWidth: "180px" }}
							size="small"
							sheetWidth="240px"
							disabled={channelsBusy}
							value={channels.find(channel => channel.value === selectedChannel) ?? channels[0]}
							onChange={value => onChannelChange(value as unknown as string)}
							values={channels}
						/>
					</FilterControl>
				)}
				{showCurrency && <FilterControl hidden={hideFilters}>
					<Select sx={{}} size="small" value={selectedCurrency} onChange={e => onCurrencyChange(e)} values={["EUR"]} />
				</FilterControl>}
			</div>
		);
	}
);
